/**
 * Kids' percussion kit: 6 large pads. No glissando (each hit is
 * intentional), but multi-touch so both hands can play.
 */

import { createSignal, For } from "solid-js";
import { SAMPLE_MANIFEST, type SoundKey } from "@melodimals/game-core";
import { cn } from "@/lib/cn";
import { useAudio } from "@/providers/audio-provider";

const PAD_STYLE: Record<string, { color: string; emoji: string }> = {
  kick: { color: "bg-rose-400", emoji: "🥁" },
  snare: { color: "bg-orange-400", emoji: "🪘" },
  tom: { color: "bg-amber-400", emoji: "🛢️" },
  woodblock: { color: "bg-lime-400", emoji: "🪵" },
  cowbell: { color: "bg-sky-400", emoji: "🔔" },
  steel: { color: "bg-violet-400", emoji: "✨" },
};

interface DrumKitProps {
  highlightedKey?: SoundKey | null;
  onNotePlayed?: (key: SoundKey) => void;
  class?: string;
}

export function DrumKit(props: DrumKitProps) {
  const audio = useAudio();
  const [pressed, setPressed] = createSignal<ReadonlySet<SoundKey>>(new Set());

  const hit = (key: SoundKey) => {
    audio.playNote("drums", key);
    props.onNotePlayed?.(key);
    setPressed((prev) => new Set(prev).add(key));
    setTimeout(() => {
      setPressed((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 120);
  };

  return (
    <div
      class={cn("grid h-full w-full grid-cols-3 gap-4 select-none", props.class)}
      style={{ "touch-action": "none" }}
    >
      <For each={SAMPLE_MANIFEST.drums}>
        {(key) => (
          <button
            type="button"
            tabIndex={-1}
            aria-label={key}
            onPointerDown={(e) => {
              e.preventDefault();
              hit(key);
            }}
            class={cn(
              "flex items-center justify-center rounded-3xl border-4 border-white/60 text-6xl shadow-lg transition-transform duration-75",
              PAD_STYLE[key]?.color ?? "bg-zinc-300",
              pressed().has(key) && "scale-90 brightness-110",
              props.highlightedKey === key && "animate-pulse ring-4 ring-white",
            )}
          >
            <span aria-hidden="true">{PAD_STYLE[key]?.emoji}</span>
          </button>
        )}
      </For>
    </div>
  );
}
